/**
 * Secure Access → App Connectors → Group detail (VSNP-1097).
 *
 * A connector group is a NAME (the join key that apps, connectors and the signed
 * bundle all reference). This page manages one group: its description, and — the
 * point of it — which connectors belong to it. A connector has exactly ONE group,
 * so "add" is a move (it leaves whatever group it was in) and "remove" sends it
 * back to the catch-all `default`.
 *
 * A group that only exists by reference (never registered) still opens here, with
 * a "Define" action to create the record so it can carry a description and be
 * managed. Batch connector configuration will grow onto this page later.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { AppWindow, ArrowLeft, Boxes, Plus, RefreshCw, Save, Trash2, Wrench } from 'lucide-react';
import StatusBadge from '../../components/common/StatusBadge';
import DataTable, { type Column } from '../../components/common/DataTable';
import Modal from '../../components/common/Modal';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import ToggleSwitch from '../../components/common/ToggleSwitch';
import ZtnaChip from '../../components/access/ZtnaChip';
import { fmtDate, nodeStatusLabel } from '../../components/access/nodeStatus';
import { useToast } from '../../components/common/ToastContainer';
import accessService, {
  type AppConnector,
  type AccessApp,
  type ConnectorGroupDetail as GroupDetail,
} from '../../services/access.service';

/** Render an app's routing target in the form that matches its type. */
function appTarget(app: AccessApp): string {
  if (app.app_type === 'external') {
    const p = app.fqdn_patterns ?? [];
    return p.length === 0 ? '—' : p.length <= 2 ? p.join(', ') : `${p.slice(0, 2).join(', ')} +${p.length - 2}`;
  }
  if (app.app_type === 'subnet') return app.target_cidr ?? '—';
  if (!app.target_host) return '—';
  return app.target_port ? `${app.target_host}:${app.target_port}` : app.target_host;
}

const DEFAULT_GROUP = 'default';

const ConnectorGroupDetail = () => {
  const params = useParams<{ name: string }>();
  const name = params.name ?? '';
  const navigate = useNavigate();
  const toast = useToast();

  const [group, setGroup] = useState<GroupDetail | null>(null);
  const [candidates, setCandidates] = useState<AppConnector[]>([]);
  const [apps, setApps] = useState<AccessApp[]>([]);
  const [appTogglingId, setAppTogglingId] = useState<string | null>(null);
  const [appDeleteTarget, setAppDeleteTarget] = useState<AccessApp | null>(null);
  const [appDeleting, setAppDeleting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [descDraft, setDescDraft] = useState('');
  const [savingDesc, setSavingDesc] = useState(false);
  const [defining, setDefining] = useState(false);

  const [addOpen, setAddOpen] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [adding, setAdding] = useState(false);

  const [removeTarget, setRemoveTarget] = useState<AppConnector | null>(null);
  const [removing, setRemoving] = useState(false);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const fetchGroup = useCallback(async () => {
    if (!name) return;
    try {
      setLoading(true);
      setError(null);
      // Group detail + the wider connector list (to offer connectors currently
      // in OTHER groups as add candidates). One list call, filtered client-side.
      const [detail, all, boundApps] = await Promise.all([
        accessService.getConnectorGroup(name),
        // 200 is the API's max page size (paginationFields cap). Enough for the
        // add-candidates picker; paginate here if a tenant ever exceeds it.
        accessService.listConnectors({ limit: 200 }),
        // Apps that target THIS group by name — managed inline below.
        accessService.listApps({ connectorGroup: name, limit: 200 }),
      ]);
      setGroup(detail);
      setDescDraft(detail.description ?? '');
      setApps(boundApps?.items ?? []);
      setCandidates(
        (all?.items ?? []).filter(
          (c) => c.connector_group !== name && c.status !== 'decommissioned',
        ),
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load the connector group');
    } finally {
      setLoading(false);
    }
  }, [name]);

  useEffect(() => {
    void fetchGroup();
  }, [fetchGroup]);

  const handleDefine = async () => {
    try {
      setDefining(true);
      await accessService.createConnectorGroup({ name, description: descDraft || undefined });
      toast.success('Group defined', name);
      await fetchGroup();
    } catch (err) {
      toast.error('Could not define group', err instanceof Error ? err.message : 'Failed');
    } finally {
      setDefining(false);
    }
  };

  const handleSaveDesc = async () => {
    if (!group?.id) return;
    try {
      setSavingDesc(true);
      await accessService.updateConnectorGroup(group.id, { description: descDraft || null });
      toast.success('Description saved', group.connectorGroup);
      await fetchGroup();
    } catch (err) {
      toast.error('Save failed', err instanceof Error ? err.message : 'Could not save the description');
    } finally {
      setSavingDesc(false);
    }
  };

  const openAdd = () => {
    setSelected(new Set());
    setAddOpen(true);
  };

  const toggleSelected = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleAdd = async () => {
    const ids = [...selected];
    if (ids.length === 0) return;
    try {
      setAdding(true);
      const results = await Promise.allSettled(
        ids.map((id) => accessService.updateConnector(id, { connectorGroup: name })),
      );
      const ok = results.filter((r) => r.status === 'fulfilled').length;
      const failed = results.length - ok;
      if (ok > 0) toast.success(`Added ${ok} connector${ok === 1 ? '' : 's'}`, name);
      if (failed > 0) {
        const firstErr = results.find((r) => r.status === 'rejected') as PromiseRejectedResult | undefined;
        toast.error(
          `${failed} could not be added`,
          firstErr?.reason instanceof Error ? firstErr.reason.message : 'Some moves were rejected',
        );
      }
      setAddOpen(false);
      await fetchGroup();
    } finally {
      setAdding(false);
    }
  };

  // Enable/disable a bound app in place — pause it without unbinding or deleting.
  const handleToggleApp = async (app: AccessApp) => {
    setAppTogglingId(app.id);
    try {
      await accessService.updateApp(app.id, { enabled: !app.enabled });
      await fetchGroup();
      toast.success(app.enabled ? 'Application disabled' : 'Application enabled', app.name);
    } catch (err) {
      toast.error('Could not update the application', err instanceof Error ? err.message : 'Update failed');
    } finally {
      setAppTogglingId(null);
    }
  };

  const handleDeleteApp = async () => {
    if (!appDeleteTarget) return;
    try {
      setAppDeleting(true);
      await accessService.deleteApp(appDeleteTarget.id);
      toast.success('Application deleted', appDeleteTarget.name);
      setAppDeleteTarget(null);
      await fetchGroup();
    } catch (err) {
      toast.error('Delete failed', err instanceof Error ? err.message : 'Could not delete the application');
    } finally {
      setAppDeleting(false);
    }
  };

  const handleRemove = async () => {
    if (!removeTarget) return;
    try {
      setRemoving(true);
      // Removing from a group means reassigning to the catch-all `default`.
      await accessService.updateConnector(removeTarget.id, { connectorGroup: DEFAULT_GROUP });
      toast.success('Connector removed', removeTarget.name ?? removeTarget.node_id);
      setRemoveTarget(null);
      await fetchGroup();
    } catch (err) {
      toast.error('Remove failed', err instanceof Error ? err.message : 'Could not remove the connector');
    } finally {
      setRemoving(false);
    }
  };

  const handleDelete = async () => {
    if (!group?.id) return;
    try {
      setDeleting(true);
      await accessService.deleteConnectorGroup(group.id);
      toast.success('Group deleted', group.connectorGroup);
      navigate('/access/connectors');
    } catch (err) {
      toast.error('Delete failed', err instanceof Error ? err.message : 'Could not delete the group');
    } finally {
      setDeleting(false);
    }
  };

  const isDefault = name === DEFAULT_GROUP;
  const memberColumns = useMemo<Column<AppConnector>[]>(
    () => [
      {
        key: 'name',
        label: 'Connector',
        sortable: true,
        render: (c) => (
          <Link to={`/access/connectors/${c.id}`} className="font-medium text-primary-600 hover:underline">
            {c.name ?? c.node_id}
          </Link>
        ),
      },
      { key: 'node_id', label: 'Node ID', render: (c) => <span className="font-mono text-xs text-gray-500">{c.node_id}</span> },
      {
        key: 'status',
        label: 'Status',
        render: (c) => <StatusBadge status={nodeStatusLabel(c.status, c.certificate_serial_number)} />,
      },
      { key: 'health', label: 'Health', render: (c) => <span className="text-sm text-gray-600">{c.health}</span> },
      { key: 'last_seen', label: 'Last seen', render: (c) => <span className="text-sm text-gray-500">{fmtDate(c.last_seen)}</span> },
      {
        key: 'actions',
        label: '',
        render: (c) =>
          isDefault ? (
            <span className="text-xs text-gray-400">—</span>
          ) : (
            <button
              type="button"
              className="p-1 text-gray-400 hover:text-danger-600"
              title="Remove from group (moves it back to 'default')"
              onClick={() => setRemoveTarget(c)}
            >
              <Trash2 className="h-4 w-4" />
            </button>
          ),
      },
    ],
    [isDefault],
  );

  if (loading && !group) {
    return <div className="py-16 text-center text-gray-500">Loading group…</div>;
  }
  if (error || !group) {
    return (
      <div className="space-y-4">
        <Link to="/access/connectors" className="inline-flex items-center gap-1 text-sm text-primary-600 hover:underline">
          <ArrowLeft className="h-4 w-4" /> App Connectors
        </Link>
        <div className="rounded-md border border-danger-200 bg-danger-50 px-4 py-3 text-sm text-danger-700">
          {error ?? 'Connector group not found'}
        </div>
      </div>
    );
  }

  const canDelete = group.registered && group.total === 0 && group.appCount === 0;

  const appColumns: Column<AccessApp>[] = [
    {
      key: 'name',
      label: 'Application',
      sortable: true,
      render: (a) => (
        <div>
          <div className="font-medium text-gray-900">{a.display_name || a.name}</div>
          <div className="text-xs text-gray-500">{a.name}</div>
        </div>
      ),
    },
    { key: 'app_type', label: 'Type', render: (a) => <span className="text-sm text-gray-700">{a.app_type}</span> },
    { key: 'target', label: 'Target', render: (a) => <span className="text-sm text-gray-600">{appTarget(a)}</span> },
    {
      key: 'actions',
      label: 'Actions',
      render: (a) => (
        <div className="flex items-center gap-3">
          <ToggleSwitch
            checked={!!a.enabled}
            busy={appTogglingId === a.id}
            onChange={() => handleToggleApp(a)}
            label={a.enabled ? `Disable ${a.name}` : `Enable ${a.name}`}
            title={a.enabled ? 'Enabled — click to disable' : 'Disabled — click to enable'}
          />
          <button
            type="button"
            className="p-1 text-gray-400 hover:text-danger-600"
            title="Delete application"
            onClick={() => setAppDeleteTarget(a)}
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <Link to="/access/connectors" className="inline-flex items-center gap-1 text-sm text-primary-600 hover:underline">
            <ArrowLeft className="h-4 w-4" /> App Connectors
          </Link>
          <div className="mt-1 flex items-center gap-3">
            <Boxes className="h-7 w-7 text-teal-600" />
            <h1 className="text-3xl font-bold text-gray-900">{group.connectorGroup}</h1>
            <ZtnaChip />
            {!group.registered && (
              <span className="rounded bg-gray-200 px-2 py-0.5 text-xs font-normal text-gray-600" title="Referenced by connectors/apps but not saved as a group. Define it to manage it.">
                not defined
              </span>
            )}
          </div>
          <p className="mt-1 text-sm text-gray-500">
            {group.total} connector{group.total === 1 ? '' : 's'} · {group.healthy} healthy · {group.appCount} app{group.appCount === 1 ? '' : 's'} target this group
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" className="btn-secondary" onClick={() => void fetchGroup()} disabled={loading}>
            <RefreshCw className={`mr-2 inline h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Definition + description */}
      <div className="card">
        <h2 className="mb-3 text-lg font-semibold text-gray-900">Group settings</h2>
        {!group.registered ? (
          <div className="space-y-3">
            <p className="text-sm text-gray-600">
              This group exists only because connectors or apps reference the name <span className="font-mono">{group.connectorGroup}</span>.
              Define it to give it a description and manage it as a first-class group.
            </p>
            <label className="block text-sm font-medium text-gray-700" htmlFor="grp-desc-new">Description</label>
            <input
              id="grp-desc-new"
              className="input-base w-full"
              placeholder="Optional — what this group is for"
              value={descDraft}
              onChange={(e) => setDescDraft(e.target.value)}
            />
            <div className="flex justify-end">
              <button type="button" className="btn-primary" onClick={() => void handleDefine()} disabled={defining}>
                {defining ? 'Defining…' : 'Define group'}
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <label className="block text-sm font-medium text-gray-700" htmlFor="grp-desc">Description</label>
            <input
              id="grp-desc"
              className="input-base w-full"
              placeholder="What this group is for"
              value={descDraft}
              onChange={(e) => setDescDraft(e.target.value)}
            />
            <div className="flex justify-end">
              <button
                type="button"
                className="btn-primary"
                onClick={() => void handleSaveDesc()}
                disabled={savingDesc || descDraft === (group.description ?? '')}
              >
                <Save className="mr-2 inline h-4 w-4" />
                {savingDesc ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Members */}
      <div className="card">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
            <AppWindow className="h-4 w-4 text-teal-600" /> Connectors in this group
          </h2>
          {!isDefault && (
            <button
              type="button"
              className="btn-secondary text-sm"
              onClick={openAdd}
              disabled={candidates.length === 0}
              title={candidates.length === 0 ? 'No other connectors available to add' : undefined}
            >
              <Plus className="mr-1 inline h-4 w-4" />
              Add connectors
            </button>
          )}
        </div>
        <DataTable
          columns={memberColumns}
          data={group.members}
          loading={loading}
          searchPlaceholder="Search connectors…"
          emptyMessage="No connectors in this group yet."
        />
      </div>

      {/* Bound applications — enable/disable in place, or delete */}
      <div className="card">
        <h2 className="mb-1 flex items-center gap-2 text-lg font-semibold text-gray-900">
          <AppWindow className="h-4 w-4 text-teal-600" /> Applications fronted by this group
        </h2>
        <p className="mb-3 text-sm text-gray-500">
          Apps that target <span className="font-mono">{group.connectorGroup}</span>. Toggle to pause an app without
          unbinding or deleting it — a disabled app is not served (only enabled apps are compiled into the policy
          bundle).
        </p>
        <DataTable
          columns={appColumns}
          data={apps}
          loading={loading}
          searchPlaceholder="Search applications…"
          emptyMessage="No applications target this group yet."
        />
      </div>

      {/* Batch config — forward-looking placeholder (VSNP-1097 scope note) */}
      <div className="card border-dashed">
        <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-400">
          <Wrench className="h-4 w-4" /> Batch configuration
        </h2>
        <p className="mt-1 text-sm text-gray-400">
          Settings applied to every connector in this group (release channel, posture policy, resource limits) will
          live here. Not available yet.
        </p>
      </div>

      {/* Danger zone — only a registered, empty, unreferenced group can be deleted */}
      {group.registered && (
        <div className="card border-danger-200">
          <h2 className="mb-2 text-lg font-semibold text-danger-700">Danger zone</h2>
          <div className="flex items-center justify-between gap-4">
            <p className="text-sm text-gray-600">
              {canDelete
                ? 'This group is empty and unreferenced. Deleting removes its record; the name can be reused.'
                : 'Delete is available only once no connector or app references this group. Move its members to another group first.'}
            </p>
            <button type="button" className="btn-danger" onClick={() => setDeleteOpen(true)} disabled={!canDelete}>
              Delete group
            </button>
          </div>
        </div>
      )}

      {/* Add connectors modal */}
      <Modal
        isOpen={addOpen}
        onClose={() => setAddOpen(false)}
        title={`Add connectors to '${group.connectorGroup}'`}
        size="lg"
        footer={
          <div className="flex justify-end gap-2">
            <button type="button" className="btn-secondary" onClick={() => setAddOpen(false)} disabled={adding}>
              Cancel
            </button>
            <button type="button" className="btn-primary" onClick={() => void handleAdd()} disabled={adding || selected.size === 0}>
              {adding ? 'Adding…' : `Add ${selected.size || ''}`.trim()}
            </button>
          </div>
        }
      >
        <p className="mb-3 text-sm text-gray-600">
          A connector belongs to one group, so adding it here <span className="font-medium">moves</span> it out of its current group.
        </p>
        {candidates.length === 0 ? (
          <p className="text-sm text-gray-500">No other connectors are available to move.</p>
        ) : (
          <div className="max-h-80 space-y-1 overflow-y-auto">
            {candidates.map((c) => (
              <label key={c.id} className="flex cursor-pointer items-center gap-3 rounded-md border border-gray-200 px-3 py-2 hover:bg-gray-50">
                <input type="checkbox" className="h-4 w-4" checked={selected.has(c.id)} onChange={() => toggleSelected(c.id)} />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium text-gray-900">{c.name ?? c.node_id}</div>
                  <div className="font-mono text-xs text-gray-400">{c.node_id}</div>
                </div>
                <span className="rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-500" title="Current group">
                  {c.connector_group}
                </span>
              </label>
            ))}
          </div>
        )}
      </Modal>

      <ConfirmDialog
        isOpen={!!appDeleteTarget}
        onClose={() => setAppDeleteTarget(null)}
        onConfirm={handleDeleteApp}
        title="Delete application"
        message={`Delete '${appDeleteTarget?.name}'? Policy rules that reference it by id must be updated first. To pause it instead, use the toggle.`}
        confirmText="Delete"
        variant="danger"
        isLoading={appDeleting}
      />

      <ConfirmDialog
        isOpen={!!removeTarget}
        onClose={() => setRemoveTarget(null)}
        onConfirm={handleRemove}
        title="Remove connector from group"
        message={`Remove '${removeTarget?.name ?? removeTarget?.node_id}' from '${group.connectorGroup}'? It moves back to the 'default' group. Refused if it is the last connector and an app still targets this group.`}
        confirmText="Remove"
        variant="danger"
        isLoading={removing}
      />

      <ConfirmDialog
        isOpen={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onConfirm={handleDelete}
        title="Delete connector group"
        message={`Delete group '${group.connectorGroup}'? This removes the group record only.`}
        confirmText="Delete"
        variant="danger"
        isLoading={deleting}
      />
    </div>
  );
};

export default ConnectorGroupDetail;
